// Auction-week sheet (#3211): the sales coming up in the next N days, by day,
// for printing and carrying to the courthouse. Pure, so the grouping rules are
// tested on their own.
//
// Only properties with a scheduled sale date in the window are listed. A sale
// is not final until its upset period closes, so the sheet shows the close time
// when it is known rather than implying the sale date settles it.

import { Property } from "../types";
import { resolveAmountOwed, resolveDisplayStatus } from "./dataQuality";

export interface AuctionSheetRow {
  property: Property;
  /** Sale time/place when the notice states them. */
  time?: string;
  location?: string;
  /** Debt or opening-bid figure, null when none is published. */
  amount: number | null;
  /** True when the figure is the original loan or a derived estimate. */
  amountIsEstimate: boolean;
  upsetCloseAt?: string;
  watched: boolean;
}

export interface AuctionSheetDay {
  /** YYYY-MM-DD. */
  date: string;
  rows: AuctionSheetRow[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** YYYY-MM-DD of an ISO timestamp or date string, in the sale's own calendar day. */
function dayKey(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m ? m[1] : null;
}

function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = Date.UTC(y, m - 1, d) + n * DAY_MS;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Sales dated from `today` through `today + days - 1`, grouped by day in date
 * order. Within a day: stated sale time first (rows without one last), then
 * address. A property counts as scheduled when its DISPLAY status is
 * auction_scheduled, so a stale row whose sale already passed is not listed.
 */
export function buildAuctionSheet(
  properties: readonly Property[],
  today: string,
  days: number,
  watchedIds: ReadonlySet<string>
): AuctionSheetDay[] {
  const last = addDays(today, days - 1);
  const byDay = new Map<string, AuctionSheetRow[]>();
  for (const p of properties) {
    if (resolveDisplayStatus(p) !== "auction_scheduled") continue;
    const key = dayKey(p.sale?.saleDate) ?? dayKey(p.auctionDate);
    if (!key || key < today || key > last) continue;
    const owed = resolveAmountOwed(p);
    const amount = owed.state === "unavailable" ? null : owed.amount;
    const row: AuctionSheetRow = {
      property: p,
      time: p.sale?.saleTime || undefined,
      location: p.sale?.saleLocation || undefined,
      amount: amount != null && amount > 0 ? amount : null,
      amountIsEstimate: owed.state === "derived",
      upsetCloseAt: p.sale?.upsetWindowCloseAt || undefined,
      watched: watchedIds.has(p.id),
    };
    const list = byDay.get(key);
    if (list) list.push(row);
    else byDay.set(key, [row]);
  }
  return Array.from(byDay.keys())
    .sort()
    .map((date) => ({
      date,
      rows: byDay.get(date)!.sort((a, b) => {
        if (!!a.time !== !!b.time) return a.time ? -1 : 1;
        if (a.time && b.time && a.time !== b.time) return a.time < b.time ? -1 : 1;
        return (a.property.address || "").localeCompare(b.property.address || "");
      }),
    }));
}
